import { useState, useEffect } from "react";
import { Box, CircularProgress, Button, Typography } from "@material-ui/core";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ExamResultOverview from "../../components/exam-result-overview/exam-result-overview";
import ReferralSharePrompt from "../../components/referral-share-prompt/ReferralSharePrompt";
import ExamResultTable from "../../components/exam-result-table/exam-result-table";
import ExamResultCategory from "../../components/exam-result-category/exam-result-category";
import {
  getQuizHistory, getQuiz, getQuestionHistory,
  getOversightQuiz, getOversightQuizHistory, getOversightQuestionHistory,
  getUserActivity, getUserPackage, resetQuestionHistory
} from "../../actions";
import { buildPracticeState } from "../../utils/practice-exam-seed";
import Menu from "../../pages/menu/menu";
import DailyLimitPanel from "../../components/daily-limit/DailyLimitPanel";
import { readStorage, useResultDailyLimit } from "../../components/daily-limit/use-daily-limit";
import { allowanceLeftLine, plansLocation } from "../../components/daily-limit/daily-limit";
import "../../components/daily-limit/daily-limit.scss";
import FirstQuizCommitment from "../../components/first-quiz-commitment/FirstQuizCommitment";
import { useFirstQuizSlot } from "../../components/first-quiz-commitment/use-first-quiz";

// The next quiz: right under the score (so nobody has to scroll past the question
// table to find it), as the page's main action, and again at the bottom for those
// who did scroll.
const NextQuiz = ({ placement, text, onPractice }) => (
  <Box component="section" className="next-quiz-block" data-testid={`next-quiz-${placement}`}
    aria-label="Next quiz">
    <Typography variant="body2" className="next-quiz-text">{text}</Typography>
    <Box className="daily-limit-actions">
      <Button variant={placement === "top" ? "contained" : "outlined"} color="primary" onClick={onPractice}>
        Practice more
      </Button>
    </Box>
  </Box>
);

export default function ExamPage() {
  const { examId, studentUserId } = useParams();
  const oversight = Boolean(studentUserId);
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [retry, setRetry] = useState(0);
  const quizLoad = useSelector((state) => state.quizDetails);
  const historyLoad = useSelector((state) => state.quizHistory);
  const questionsLoad = useSelector((state) => state.questionHistory);
  const quizDetails = quizLoad?.quizDetails;
  const quizHistory = historyLoad?.quizHistory;
  const questions = questionsLoad?.questionHistory;

  // Each read must belong to this route and viewing context. Never copy a
  // previous quiz or student's result into local state while a new read loads.
  const matchesRoute = (load) => load?.quizId === examId &&
    load?.studentUserId === (oversight ? studentUserId : null);
  const validScore = !!quizHistory &&
    [quizHistory.correctPercentage, quizHistory.questionCorrectCount,
      quizHistory.questionAttemptCount].every(Number.isFinite);
  const reads = [
    [quizLoad, quizDetails?.id === examId],
    [historyLoad, validScore],
    [questionsLoad, Array.isArray(questions)],
  ];
  const failed = reads.some(([load, valid]) =>
    matchesRoute(load) && load.loading === false && !valid);
  const ready = reads.every(([load, valid]) =>
    matchesRoute(load) && load.loading === false && valid);
  // The student's own finished quiz: the moment for the next-quiz button and
  // the invite prompt. Never in oversight, where a tutor or mentor is viewing.
  const finishedOwnQuiz = ready && !oversight && quizDetails.quizStatus === "COMPLETE";
  // Today's allowance, read only right after the student submits (exam.jsx sets
  // quizFinished), not when they browse old results from history, and only for
  // students: the count reports 0 left for tutors and admins, who are uncapped.
  const studentJustFinished = finishedOwnQuiz && location.state?.quizFinished === true
    && readStorage("role") === "ROLE_STUDENT";
  const dailyLimit = useResultDailyLimit({
    enabled: studentJustFinished,
    userId: readStorage("userId"),
    examTypes: quizDetails?.categoryMap?.examType,
  });
  const allowanceUsedUp = dailyLimit.status === "ready" && dailyLimit.info.exhausted;
  // A new student's first completed quiz gets the "come back tomorrow" block in
  // place of the ordinary next step (use-first-quiz.js).
  const firstQuiz = useFirstQuizSlot({ studentJustFinished, userId: readStorage("userId"), examId, dailyLimit });
  // With the allowance read, the top line says how many are left today.
  const nextQuizText = dailyLimit.status === "ready" && !allowanceUsedUp
    ? allowanceLeftLine(dailyLimit.info)
    : "Choose your next quiz.";
  const onPractice = () => {
    const state = buildPracticeState(quizDetails?.categoryMap?.examType);
    if (state) history.push("/create-quiz", state);
    else history.push("/create-quiz");
  };

  useEffect(() => () => dispatch(resetQuestionHistory()), [dispatch]);

  useEffect(() => {
    dispatch(resetQuestionHistory());
    if (oversight) {
      // Oversight remains read-only: use proxy reads and never fetch the
      // viewer's own activity/package or create their feedback records.
      const timer = setTimeout(() => {
        dispatch(getOversightQuestionHistory({ studentUserId, quizId: examId }));
      }, 300);
      dispatch(getOversightQuiz({ studentUserId, quizId: examId }));
      dispatch(getOversightQuizHistory({ studentUserId, quizId: examId }));
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => dispatch(getQuestionHistory(examId)), 300);
    dispatch(getQuiz(examId));
    dispatch(getQuizHistory(examId));
    const savedUserId = window.localStorage.getItem("userId");
    if (savedUserId && savedUserId !== "undefined") {
      dispatch(getUserActivity(savedUserId));
      dispatch(getUserPackage(savedUserId));
    }
    return () => clearTimeout(timer);
  }, [dispatch, examId, oversight, studentUserId, retry]);

  return (
    <Box component="div" className="body-cont-over">
      <Menu />
      <Box component="div" className="exam-result">
        {oversight && (
          <Box component="div" className="oversight-banner" data-testid="oversight-banner">
            Viewing {location.state?.studentName || "this student"}'s quiz — read only
          </Box>
        )}
        {!ready && <Typography variant="h1" className="quiz-result-title">Quiz Results</Typography>}
        {failed ? (
          <Box role="alert" p={3}>
            <Typography>Couldn't load this quiz result. Please try again.</Typography>
            <Button variant="outlined" onClick={() => setRetry(value => value + 1)}>Retry</Button>
          </Box>
        ) : !ready ? (
          <Box role="status" aria-live="polite" p={3} display="flex" alignItems="center">
            <CircularProgress size={24} aria-hidden="true" />
            <Box ml={2}>Loading quiz results…</Box>
          </Box>
        ) : (
          <>
            <ExamResultOverview examId={examId} quizOverview={quizHistory} quizDetails={quizDetails} />
            {/* The next step sits right under the score. It is the same for every
                viewer, so the invite prompt below moves down by the same amount
                for everyone. While today's allowance is being read, nothing shows
                here (the bottom button is there meanwhile). */}
            {firstQuiz.show && (
              <FirstQuizCommitment
                examId={examId}
                info={dailyLimit.info}
                examTypes={quizDetails?.categoryMap?.examType}
                reminder={firstQuiz.reminder}
                userId={readStorage("userId")}
                onStartNext={onPractice}
                onSeePlans={(planId) => history.push(plansLocation(planId))}
                onDismiss={firstQuiz.dismiss}
                dailyLimitShownRef={firstQuiz.dailyLimitShownRef}
              />
            )}
            {finishedOwnQuiz && allowanceUsedUp && !firstQuiz.held && (
              <Box component="section" className="daily-limit-card" data-testid="daily-limit-card"
                aria-labelledby="daily-limit-card-title">
                <DailyLimitPanel
                  surface="exam_result"
                  info={dailyLimit.info}
                  onSeePlans={(planId) => history.push(plansLocation(planId))}
                  titleId="daily-limit-card-title"
                  shownRef={firstQuiz.dailyLimitShownRef}
                />
              </Box>
            )}
            {finishedOwnQuiz && !allowanceUsedUp && dailyLimit.status !== "loading" && !firstQuiz.held && (
              <NextQuiz placement="top" text={nextQuizText} onPractice={onPractice} />
            )}
            {finishedOwnQuiz && (
              <ReferralSharePrompt
                score={quizHistory.correctPercentage}
                absite={Boolean(buildPracticeState(quizDetails?.categoryMap?.examType))}
              />
            )}
            <ExamResultCategory quizCategoryData={quizHistory} />
            <ExamResultTable examId={examId} questions={questions}
              oversightStudentUserId={oversight ? studentUserId : undefined} />
            {/* With today's allowance used up, "Practice more" would only open the
                out-of-questions modal, so the card above replaces it. */}
            {finishedOwnQuiz && !allowanceUsedUp && (
              <NextQuiz placement="bottom" text="Choose your next quiz." onPractice={onPractice} />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
